import { createTheme, responsiveFontSizes } from "@mui/material";
import { StyledComponentProps } from "../models/global/Types";
import { defaultColors, themeColors, contrastColors } from "./themeColors";

declare module "@mui/material/styles" {
  interface Theme {
    color: {
      dark: string;
      main: string;
      lighter: string;
      light: string;
      textColor: string;
      contrastColor: string;
      background: string;
    };
    contrast: {
      dark: string;
      main: string;
      lighter: string;
      light: string;
      shadowColor: string;
    };
  }
  interface ThemeOptions {
    color?: {
      dark?: string;
      main?: string;
      lighter?: string;
      light?: string;
      textColor?: string;
      contrastColor?: string;
    };
    contrast?: {
      dark?: string;
      main?: string;
      lighter?: string;
      light?: string;
      shadowColor?: string;
    };
  }
  interface BreakpointOverrides {
    xs: true;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobile: true;
    tablet: true;
    laptop: true;
    desktop: true,
    wide: true, 
  }
}

const defaultTheme = {
  breakpoints: {
    values: {
    mobile: 0,
    tablet: 768,
    laptop: 1024,
    desktop: 1280,
    wide: 1536, 
  },
  },
  palette: {
    error: {
      main: defaultColors.red,
    },
    success: {
      main: defaultColors.green,
    },
    warning: {
      main: defaultColors.orange,
    },
    info: {
      main: defaultColors.purple,
    },
  },
  typography: {
    h1: {
      fontFamily: "'Product Sans', sans-serif",
      fontSize: "4rem",
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: "0.0625rem",
    },
    h2: {
      fontFamily: "'Product Sans', sans-serif",
      fontSize: "2.25rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h3: {
      fontFamily: "'Product Sans', sans-serif",
      fontSize: "1.5rem",
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: "0.0625rem",
    },
    h4: {
      fontFamily: "'Product Sans', sans-serif",
      fontSize: "1.125rem",
      fontWeight: 400,
      lineHeight: 1.5,
    },
    h5: {
      fontFamily: "'Product Sans', sans-serif",
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: "'Product Sans', sans-serif",
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: "'Product Sans', sans-serif",
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.4,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme }: StyledComponentProps) => ({
          padding: "0.5rem 2rem",
          fontFamily: "'Product Sans', sans-serif",
          fontWeight: "bold",
          background: theme.color.main,
          color: theme.color.background,
          borderRadius: "10px",
          transition: ".5s",
          width: "fit-content",
          "&:hover": {
            background: theme.color.contrastColor,
          },
          "&:active": {},
          "&:focus": {},
          "&:focus-visible": {},
        }),
        outlined: ({ theme }: StyledComponentProps) => ({
          padding: "0.5rem 2rem",
          fontFamily: "'Product Sans', sans-serif",
          fontWeight: "bold",
          background: "none",
          color: theme.color.main,
          border: `2px solid ${theme.color.main}`,
          borderRadius: "10px",
          transition: ".5s",
          width: "fit-content",
          "&:hover": {
            background: theme.color.main,
            border: `2px solid ${theme.color.main}`,
            color: theme.color.background,
          },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: StyledComponentProps) => ({
          ".MuiInputBase-root": {
            color: theme.color.dark,
            margin: "0",
            width: "fit-content",
            input: {
              padding: ".5rem 1rem",
            },
          },
          ".MuiFormLabel-root": {
            display: "none",
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: StyledComponentProps) => ({
          background: theme.contrast.lighter,
          color: theme.color.dark,
          fontWeight: "bold",
          fontSize: "1rem",
          "&.MuiInputBase-multiline": {
            padding: ".5rem 1rem",
          },
        }),
        notchedOutline: {
          border: "none",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }: StyledComponentProps) => ({
          width: "50%",
          border: `2px solid ${theme.color.main}`,
          color: theme.color.main,
          fontWeight: "bold",
          "&.Mui-selected": {
            backgroundColor: theme.color.main,
            color: theme.color.background,
          },
          "&:hover": {
            backgroundColor: theme.color.dark,
            color: theme.color.background,
          },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          width: "30rem",
          display: "flex",
          justifyContent: "center",
        },
      },
    },
  },
};

let theme = createTheme(
  {
    color: themeColors,
    contrast: contrastColors,
  },
  defaultTheme,
);

theme = responsiveFontSizes(theme, {
  factor: 2, // stronger scaling
  breakpoints: ["mobile", "tablet", "laptop"],
});

export { theme };
